/**
 * Применение sync-события к локальной реплике (CC §4.6).
 *
 * Ключевые правила, которые здесь реализованы дословно:
 *  - идемпотентность по `SyncEvent.id` (шаг 1);
 *  - неизвестный тип пропускается, но cursor сохраняется (шаг 2);
 *  - версионное отсечение по `entity_version` для версионируемых сущностей (шаг 4);
 *  - применение и запись `sync_state.cursor` — в ОДНОЙ транзакции (§4.4 п. 2).
 */
import type {
  AccountStatusChangedData,
  Channel,
  ChannelDeletedData,
  Contact,
  Conversation,
  ConversationUpdatedData,
  CounterpartUpdatedData,
  Match,
  MatchClosedData,
  MediaReadyData,
  MediaRejectedData,
  MeUpdatedData,
  Message,
  MessageDeletedData,
  MessageEditedData,
  MessageReactionChangedData,
  Notification,
  NotificationReadData,
  ReceiptAdvancedData,
  SessionRevokedData,
  SyncEvent,
  UserUnavailableData,
} from '@shared/api/types'
import {
  blocksRepo,
  commitCursorInTransaction,
  contactsRepo,
  conversationsRepo,
  matchesRepo,
  mediaRepo,
  messagesRepo,
  notificationsRepo,
  syncStateRepo,
  userDb,
  type MeRecord,
} from '@shared/db'

/** Побочные эффекты, которые обязано выполнить приложение (не реплика). */
export interface ApplyEffects {
  /** Сессия отозвана — SYS-07 (CC §4.11). */
  sessionRevoked?: string
  /** Статус аккаунта изменился — SYS-08 / SYS-09 / O-07. */
  accountStatus?: AccountStatusChangedData
  /** Требуются согласия — SYS-05. */
  consentRequired?: boolean
  /** Создано новое совпадение — экран «Это совпадение» (M-02). */
  matchCreated?: Match
  /** Пришло новое сообщение не от меня — звук/бейдж. */
  incomingMessage?: Message
  /** Медиа стало доступно — можно отправлять ожидающее сообщение. */
  mediaReady?: string
  mediaRejected?: string
  /** URL медиа, которые нужно выбросить из кэша service worker (§9.3 п. 7). */
  invalidateMediaUrls?: string[]
  /**
   * §8.5 п. 5: по `block.created`, `user.unavailable` и `match.closed` с
   * показанных системных уведомлений этой пары снимается показ.
   */
  dismissNotifications?: { conversationId?: string; matchId?: string; userId?: string }[]
}

/** Накопление целей для §8.5 п. 5 — событий у пары может быть несколько. */
function queueDismiss(
  effects: ApplyEffects,
  target: { conversationId?: string; matchId?: string; userId?: string },
): void {
  ;(effects.dismissNotifications ??= []).push(target)
}

/**
 * Сущности, у которых в REST-схеме есть поле `version` — только к ним
 * применяется версионное отсечение (CC §4.6, «Версионируемые сущности»).
 */
const VERSIONED_TYPES = new Set<SyncEvent['type']>([
  'me.updated',
  'match.created',
  'conversation.updated',
  'receipt.advanced',
  'message.created',
  'message.edited',
  'message.deleted',
  'message.reaction.changed',
  'notification.created',
  'channel.updated',
])

export interface ApplyContext {
  userId: string
}

/**
 * Применяет одно событие. Возвращает побочные эффекты для приложения.
 * Любой исход сохраняет `cursor` — включая пропуск по дедупликации и по версии.
 */
export async function applyEvent(event: SyncEvent, ctx: ApplyContext): Promise<ApplyEffects> {
  const effects: ApplyEffects = {}
  const db = userDb()
  // Шаг 1: дедупликация по id.
  if (await syncStateRepo.isApplied(ctx.userId, event.id)) {
    await db.transaction('rw', db.sync_state, async () => {
      await commitCursorInTransaction(ctx.userId, event.cursor, null)
    })
    return effects
  }
  await db.transaction(
    'rw',
    [
      db.me,
      db.conversations,
      db.messages,
      db.matches,
      db.notifications,
      db.blocked_user_ids,
      db.media_cache,
      db.sync_state,
      db.pending_ops,
    ],
    async () => {
      // Шаг 2: неизвестный тип — пропуск с сохранением cursor.
      if (!isKnownType(event.type)) {
        await commitCursorInTransaction(ctx.userId, event.cursor, event.id)
        return
      }
      // Шаги 3–4: версионное отсечение.
      if (VERSIONED_TYPES.has(event.type)) {
        const stale = await isStale(event, ctx)
        if (stale) {
          await commitCursorInTransaction(ctx.userId, event.cursor, event.id)
          return
        }
      }
      // Шаг 5: применение.
      await dispatch(event, ctx, effects)
      // Шаг 5 (окончание): cursor пишется той же транзакцией.
      await commitCursorInTransaction(ctx.userId, event.cursor, event.id)
    },
  )
  return effects
}

function isKnownType(type: string): type is SyncEvent['type'] {
  return KNOWN_TYPES.has(type)
}

const KNOWN_TYPES = new Set<string>([
  'me.updated',
  'match.created',
  'match.closed',
  'block.created',
  'block.removed',
  'user.unavailable',
  'counterpart.updated',
  'conversation.updated',
  'message.created',
  'message.edited',
  'message.deleted',
  'message.reaction.changed',
  'receipt.advanced',
  'notification.created',
  'notification.read',
  'media.ready',
  'media.rejected',
  'session.revoked',
  'account.status_changed',
  'contact.created',
  'contact.deleted',
  'channel.updated',
  'channel.deleted',
])

/* ===================================================================== */
/* Версионное отсечение                                                   */

/* ===================================================================== */
async function isStale(event: SyncEvent, ctx: ApplyContext): Promise<boolean> {
  const db = userDb()
  switch (event.entity_type) {
    case 'user': {
      if (event.type !== 'me.updated') return false
      const me = await db.me.get(ctx.userId)
      return me !== undefined && me.version >= event.entity_version
    }
    case 'match': {
      if (!event.entity_id) return false
      const match = await db.matches.get(event.entity_id)
      return match !== undefined && match.version >= event.entity_version
    }
    case 'conversation': {
      if (!event.entity_id) return false
      const conversation = await db.conversations.get(event.entity_id)
      return conversation !== undefined && conversation.version >= event.entity_version
    }
    case 'message': {
      if (!event.entity_id) return false
      const message = await messagesRepo.byId(event.entity_id)
      return message !== undefined && message.version >= event.entity_version
    }
    case 'notification': {
      // `notification.read` не привязано к одному объекту — версия не сравнивается.
      if (!event.entity_id) return false
      const notification = await db.notifications.get(event.entity_id)
      return notification !== undefined && notification.version >= event.entity_version
    }
    case 'channel': {
      // Изменение v1.7: карточка канала живёт в ленте, поэтому версия сравнивается с той,
      // что уже лежит в `Conversation.channel` (id ленты известен из самого события).
      if (event.type !== 'channel.updated') return false
      const feed = await db.conversations.get((event.data as Channel).conversation_id)
      return (
        feed?.channel !== undefined && feed.channel !== null && feed.channel.version >= event.entity_version
      )
    }
    default:
      return false
  }
}

/* ===================================================================== */
/* Диспетчер                                                              */

/* ===================================================================== */
async function dispatch(event: SyncEvent, ctx: ApplyContext, effects: ApplyEffects): Promise<void> {
  switch (event.type) {
    case 'me.updated':
      await applyMeUpdated(event.data as MeUpdatedData, event.entity_version, ctx, effects)
      break
    case 'match.created':
      await applyMatchCreated(event.data as Match, effects)
      break
    case 'match.closed':
      await applyMatchClosed(event.data as MatchClosedData, effects)
      break
    case 'block.created':
      await applyBlockChanged(event.entity_id, true)
      if (event.entity_id) queueDismiss(effects, { userId: event.entity_id })
      break
    case 'block.removed':
      await applyBlockChanged(event.entity_id, false)
      break
    case 'user.unavailable':
      await applyUserUnavailable(event.data as UserUnavailableData, effects)
      break
    case 'counterpart.updated':
      await applyCounterpartUpdated(event.data as CounterpartUpdatedData)
      break
    case 'conversation.updated':
      await applyConversationUpdated(event.data as ConversationUpdatedData, event.entity_version, ctx)
      break
    case 'message.created':
      await applyMessageCreated(event.data as Message, ctx, effects)
      break
    case 'message.edited':
      await applyMessageEdited(event.data as MessageEditedData)
      break
    case 'message.deleted':
      await applyMessageDeleted(event.data as MessageDeletedData, effects)
      break
    case 'message.reaction.changed':
      await applyReactionChanged(event.data as MessageReactionChangedData)
      break
    case 'receipt.advanced':
      await applyReceiptAdvanced(event.data as ReceiptAdvancedData, ctx)
      break
    case 'notification.created':
      await applyNotificationCreated(event.data as Notification, ctx)
      break
    case 'notification.read':
      await applyNotificationRead(event.data as NotificationReadData, ctx)
      break
    case 'media.ready':
      await applyMediaReady(event.data as MediaReadyData, effects)
      break
    case 'media.rejected':
      await applyMediaRejected(event.data as MediaRejectedData, effects)
      break
    case 'session.revoked':
      effects.sessionRevoked = (event.data as SessionRevokedData).session_id
      break
    case 'account.status_changed':
      await applyAccountStatusChanged(event.data as AccountStatusChangedData, ctx, effects)
      break
    // Изменение v1.6: контакты — односторонний список владельца, событие приходит только ему
    case 'contact.created':
      await contactsRepo.put(event.data as Contact)
      break
    case 'contact.deleted':
      if (event.entity_id) await contactsRepo.remove(event.entity_id)
      break
    case 'channel.updated':
      await applyChannelUpdated(event.data as Channel)
      break
    case 'channel.deleted':
      await conversationsRepo.delete((event.data as ChannelDeletedData).conversation_id)
      break
    default:
      break
  }
}

/* ===================================================================== */
/* Обработчики                                                            */

/* ===================================================================== */

/**
 * Карточка канала живёт внутри его ленты (`Conversation.channel`, изменение v1.7): отдельной
 * таблицы у реплики нет, поэтому событие обновляет поле диалога. Пока пользователь не подписан,
 * ленты в реплике нет — тогда событие не о чем применять, карточка придёт вместе с подпиской.
 */
async function applyChannelUpdated(channel: Channel): Promise<void> {
  const feed = await conversationsRepo.get(channel.conversation_id)
  if (!feed) return
  await conversationsRepo.patchOptimistic(feed.id, { channel })
}
async function applyMeUpdated(
  data: MeUpdatedData,
  version: number,
  ctx: ApplyContext,
  effects: ApplyEffects,
): Promise<void> {
  const table = userDb().me
  const existing = await table.get(ctx.userId)
  if (!existing) return
  const next: MeRecord = { ...existing, version }
  if (data.profile) next.profile = data.profile
  if (data.settings) next.settings = data.settings
  if (data.modes) next.modes = data.modes
  if (data.user?.status) next.user = { ...existing.user, status: data.user.status }
  if (data.consents_required) {
    next.consents_required = data.consents_required
    if (data.consents_required.length > 0) effects.consentRequired = true
  }
  /*
   * Сервер шлёт в дельте и блоки, которых в `MeUpdatedData` нет (способы входа
   * после `identity_added`, санкции): открытые объекты BS §16.2. Без них
   * запись реплики после события оставалась бы со старым списком.
   */
  const extra = data as { identities?: unknown; sanctions?: unknown }
  if (Array.isArray(extra.identities)) next.identities = extra.identities as MeRecord['identities']
  if (Array.isArray(extra.sanctions)) next.sanctions = extra.sanctions as MeRecord['sanctions']
  await table.put(next)
}

async function applyMatchCreated(data: Match, effects: ApplyEffects): Promise<void> {
  const applied = await matchesRepo.put(data)
  if (applied) effects.matchCreated = data
}

async function applyMatchClosed(data: MatchClosedData, effects: ApplyEffects): Promise<void> {
  const db = userDb()
  // Агрегат ищется и по первичному id, и по режимному (GAP-ALL-07).
  const aggregate = (await db.matches.get(data.match_id)) ?? (await matchesRepo.byModeMatchId(data.match_id))
  if (!aggregate) return

  // §8.5 п. 5: показанные уведомления закрытой пары снимаются.
  if (!data.remaining_modes.length) {
    queueDismiss(effects, {
      matchId: data.match_id,
      userId: aggregate.counterpart.id,
      ...(aggregate.conversation_id ? { conversationId: aggregate.conversation_id } : {}),
    })
  }
  const remaining = aggregate.matches.filter((m) => m.id !== data.match_id)
  await db.matches.put({
    ...aggregate,
    matches: remaining,
    // Денормализация для индекса `*match_ids` обязана идти следом (GAP-ALL-07).
    match_ids: remaining.map((m) => m.id),
    modes: data.remaining_modes,
    state: data.remaining_modes.length ? 'active' : 'closed',
  })
}

async function applyBlockChanged(userId: string | null, blocked: boolean): Promise<void> {
  if (!userId) return
  if (blocked) await blocksRepo.add(userId, new Date().toISOString())
  else await blocksRepo.remove(userId)
}

async function applyUserUnavailable(data: UserUnavailableData, effects: ApplyEffects): Promise<void> {
  const db = userDb()
  const invalidate: string[] = []

  // §8.5 п. 5: показанные уведомления недоступного пользователя снимаются.
  for (const conversationId of data.conversation_ids) queueDismiss(effects, { conversationId })
  for (const matchId of data.match_ids) queueDismiss(effects, { matchId })

  // ПС §10: «Профиль недоступен» без раскрытия причины.
  for (const conversationId of data.conversation_ids) {
    const conversation = await db.conversations.get(conversationId)
    // У ленты канала контрагента нет (изменение v1.7) — скрывать нечего.
    if (!conversation?.counterpart) continue
    for (const variant of conversation.counterpart.photo?.variants ?? []) invalidate.push(variant.url)
    await db.conversations.put({
      ...conversation,
      counterpart: {
        ...conversation.counterpart,
        availability: 'unavailable',
        photo: null,
        presence: null,
      },
    })
  }
  for (const matchId of data.match_ids) {
    const match = await db.matches.get(matchId)
    if (!match) continue
    await db.matches.put({
      ...match,
      counterpart: {
        ...match.counterpart,
        availability: 'unavailable',
        photos: [],
      },
    })
  }
  if (invalidate.length) effects.invalidateMediaUrls = invalidate
}

async function applyCounterpartUpdated(data: CounterpartUpdatedData): Promise<void> {
  const db = userDb()
  const conversation = await db.conversations.where('counterpart.id').equals(data.user_id).first()
  if (conversation?.counterpart) {
    const counterpart = { ...conversation.counterpart }
    if (data.display_name !== undefined) counterpart.display_name = data.display_name
    if (data.photo !== undefined) counterpart.photo = data.photo
    if (data.username !== undefined) counterpart.username = data.username
    if (data.availability !== undefined) counterpart.availability = data.availability
    await db.conversations.put({ ...conversation, counterpart })
  }
  const matches = await db.matches.toArray()
  for (const match of matches) {
    if (match.counterpart.id !== data.user_id) continue
    const counterpart = { ...match.counterpart }
    if (data.display_name !== undefined) counterpart.display_name = data.display_name
    if (data.availability !== undefined) counterpart.availability = data.availability
    if (data.username !== undefined) counterpart.username = data.username
    if (data.photo !== undefined) counterpart.photos = data.photo ? [data.photo] : []
    await db.matches.put({ ...match, counterpart })
  }
  // Подписи авторов входят в читаемую историю. Изменение/удаление профиля должно
  // обновлять и эти проекции, включая цитаты чужих сообщений.
  await db.messages
    .filter((message) => message.sender?.id === data.user_id || message.reply_to?.sender?.id === data.user_id)
    .modify((message) => {
      for (const sender of [message.sender, message.reply_to?.sender]) {
        if (sender?.id !== data.user_id) continue
        if (data.availability === 'deleted' || data.availability === 'unavailable') {
          sender.display_name = null
          sender.photo = null
        } else {
          if (data.display_name !== undefined) sender.display_name = data.display_name
          if (data.photo !== undefined) sender.photo = data.photo
        }
      }
    })
  if (
    data.display_name !== undefined ||
    data.availability === 'deleted' ||
    data.availability === 'unavailable'
  ) {
    await db.conversations
      .filter((item) => item.last_message?.sender_id === data.user_id)
      .modify((item) => {
        if (item.last_message)
          item.last_message.sender_name =
            data.availability === 'deleted' || data.availability === 'unavailable'
              ? null
              : (data.display_name ?? null)
      })
  }
}

async function applyConversationUpdated(
  data: ConversationUpdatedData,
  version: number,
  ctx: ApplyContext,
): Promise<void> {
  const db = userDb()
  const existing = await db.conversations.get(data.conversation_id)
  if (!existing) {
    // Диалог ещё не загружен: дельты недостаточно, список перечитает движок.
    return
  }
  const next: Conversation = { ...existing, version }
  if (data.state !== undefined) next.state = data.state
  if (data.direct !== undefined) next.direct = data.direct
  if (data.modes !== undefined) next.modes = data.modes
  if (data.matches !== undefined) next.matches = data.matches
  if (data.counterpart !== undefined) next.counterpart = data.counterpart
  if (data.last_message !== undefined) next.last_message = data.last_message
  if (data.pinned_message !== undefined) next.pinned_message = data.pinned_message
  if (data.last_seq !== undefined) next.last_seq = data.last_seq
  if (data.unread_count !== undefined) next.unread_count = data.unread_count
  if (data.match_seen !== undefined) next.match_seen = data.match_seen
  if (data.updated_at !== undefined) next.updated_at = data.updated_at
  if (data.my) {
    next.my = {
      last_read_seq: data.my.last_read_seq ?? existing.my.last_read_seq,
      last_delivered_seq: data.my.last_delivered_seq ?? existing.my.last_delivered_seq,
      muted_until: data.my.muted_until !== undefined ? data.my.muted_until : existing.my.muted_until,
      archived_at: data.my.archived_at !== undefined ? data.my.archived_at : existing.my.archived_at,
    }
  }
  if (data.counterpart_receipts) {
    next.counterpart_receipts = {
      last_delivered_seq:
        data.counterpart_receipts.last_delivered_seq ??
        existing.counterpart_receipts?.last_delivered_seq ??
        0,
      last_read_seq:
        data.counterpart_receipts.last_read_seq !== undefined
          ? data.counterpart_receipts.last_read_seq
          : (existing.counterpart_receipts?.last_read_seq ?? null),
    }
  }
  await db.conversations.put(next)
  // CC §5.3 правило 1: статусы пересчитываются при КАЖДОМ получении Conversation.
  await messagesRepo.recomputeOwnStatuses(next.id, ctx.userId, next.counterpart_receipts)
  /*
   * CC §4.9 п. 11: входящее сообщение снимает архив на сервере, поэтому
   * отложенный `PATCH {archived:true}` обязан быть снят с очереди.
   */
  if (data.my?.archived_at === null && (data.unread_count ?? 0) > 0) {
    const { queueRepo } = await import('@shared/db/repositories/queue')
    await queueRepo.dropPendingArchive(next.id)
  }
}

async function applyMessageCreated(data: Message, ctx: ApplyContext, effects: ApplyEffects): Promise<void> {
  const conversation = await userDb().conversations.get(data.conversation_id)
  // Своё сообщение: снимаем неподтверждённую запись, ставим настоящую.
  if (data.client_message_id) {
    await messagesRepo.confirmSent(
      data.client_message_id,
      data,
      ctx.userId,
      conversation?.counterpart_receipts,
    )
  } else {
    await messagesRepo.putConfirmed(data, ctx.userId, conversation?.counterpart_receipts)
  }
  // Комментарий под постом канала (изменение v1.8) не считается входящим сообщением диалога:
  // ленту он не поднимает и баннер о нём не показывается — счётчик под постом обновляется.
  if (data.comment_of) {
    await messagesRepo.bumpComments(data.comment_of, 1)
    return
  }
  if (data.sender_id !== ctx.userId) effects.incomingMessage = data

  // Первое сообщение снимает совпадение с ленты «новых» (ПС §10): отдельного
  // события об этом нет, поэтому признак правится по факту в реплике.
  const match = await matchesRepo.byConversation(data.conversation_id)
  if (match && !match.has_messages) await userDb().matches.put({ ...match, has_messages: true })
}

async function applyMessageEdited(data: MessageEditedData): Promise<void> {
  const db = userDb()
  const key: [string, number] = [data.conversation_id, data.seq]
  const existing = await db.messages.get(key)
  if (!existing) return
  await db.messages.put({
    ...existing,
    text: data.text,
    edited_at: data.edited_at,
    version: data.version,
  })
}

async function applyMessageDeleted(data: MessageDeletedData, effects: ApplyEffects): Promise<void> {
  const db = userDb()
  const key: [string, number] = [data.conversation_id, data.seq]
  const existing = await db.messages.get(key)
  if (!existing) return
  const invalidate = (existing.media?.variants ?? []).map((v) => v.url)
  if (data.scope === 'me') {
    // «Удалить у меня» — запись исчезает локально (CC §5.3).
    await db.messages.delete(key)
  } else {
    // «Для всех» — нейтральная отметка вместо содержимого (ПС §11).
    await db.messages.put({
      ...existing,
      text: null,
      media: null,
      reactions: [],
      deleted: true,
      version: data.version,
      local_state: 'tombstone',
    })
  }
  if (invalidate.length) {
    effects.invalidateMediaUrls = [...(effects.invalidateMediaUrls ?? []), ...invalidate]
  }
}

async function applyReactionChanged(data: MessageReactionChangedData): Promise<void> {
  const db = userDb()
  const key: [string, number] = [data.conversation_id, data.seq]
  const existing = await db.messages.get(key)
  if (!existing) return
  // BS §4.2: одна реакция на пользователя.
  const reactions = existing.reactions.filter((r) => r.user_id !== data.user_id)
  if (data.reaction !== null) reactions.push({ user_id: data.user_id, reaction: data.reaction })
  await db.messages.put({ ...existing, reactions, version: data.version })
}

async function applyReceiptAdvanced(data: ReceiptAdvancedData, ctx: ApplyContext): Promise<void> {
  const db = userDb()
  const conversation = await db.conversations.get(data.conversation_id)
  if (!conversation) return
  // Событие о моём собственном курсоре — двигаем свою часть.
  if (data.user_id === ctx.userId) {
    await db.conversations.put({
      ...conversation,
      my: {
        ...conversation.my,
        last_delivered_seq: Math.max(conversation.my.last_delivered_seq, data.delivered_seq),
        last_read_seq: Math.max(conversation.my.last_read_seq, data.read_seq ?? 0),
      },
    })
    return
  }
  // Курсоры контрагента: двигаем только вперёд (CC §5.3 правило 2).
  const next = {
    last_delivered_seq: Math.max(
      conversation.counterpart_receipts?.last_delivered_seq ?? 0,
      data.delivered_seq,
    ),
    last_read_seq:
      data.read_seq === null || data.read_seq === undefined
        ? (conversation.counterpart_receipts?.last_read_seq ?? null)
        : Math.max(conversation.counterpart_receipts?.last_read_seq ?? 0, data.read_seq),
  }
  await db.conversations.put({ ...conversation, counterpart_receipts: next })
  await messagesRepo.recomputeOwnStatuses(conversation.id, ctx.userId, next)
}

async function applyNotificationCreated(data: Notification, ctx: ApplyContext): Promise<void> {
  const applied = await notificationsRepo.put(data)
  if (!applied) return
  // GAP-COMMON-05, обходной путь CC §5.9 п. 2: счётчик правится на +1.
  await adjustUnreadNotifications(ctx.userId, 1)
}

async function applyNotificationRead(data: NotificationReadData, ctx: ApplyContext): Promise<void> {
  const db = userDb()
  let changed = 0
  for (const id of data.notification_ids) {
    const existing = await db.notifications.get(id)
    if (!existing || existing.read_at !== null) continue
    await db.notifications.put({
      ...existing,
      read_at: new Date().toISOString(),
    })
    changed += 1
  }
  if (changed) await adjustUnreadNotifications(ctx.userId, -changed)
}

async function adjustUnreadNotifications(userId: string, delta: number): Promise<void> {
  const table = userDb().me
  const me = await table.get(userId)
  if (!me) return
  await table.put({
    ...me,
    unread: {
      ...me.unread,
      notifications: Math.max(0, me.unread.notifications + delta),
    },
  })
}

async function applyMediaReady(data: MediaReadyData, effects: ApplyEffects): Promise<void> {
  await mediaRepo.put({
    id: data.media_id,
    kind: data.purpose === 'chat_voice' ? 'audio' : data.purpose === 'chat_video' ? 'video' : 'image',
    state: 'ready',
    variants: data.variants,
  })
  effects.mediaReady = data.media_id
}

async function applyMediaRejected(data: MediaRejectedData, effects: ApplyEffects): Promise<void> {
  await mediaRepo.delete(data.media_id)
  effects.mediaRejected = data.media_id
}

async function applyAccountStatusChanged(
  data: AccountStatusChangedData,
  ctx: ApplyContext,
  effects: ApplyEffects,
): Promise<void> {
  const table = userDb().me
  const me = await table.get(ctx.userId)
  if (me) {
    await table.put({
      ...me,
      user: { ...me.user, status: data.status },
      sanctions: data.sanctions,
    })
  }
  effects.accountStatus = data
}
